import { useState } from "react";

import type { TaxMapping } from "../../api/types";
import { Badge, Table, type Column } from "../../components";
import { t } from "../../i18n/t";
import { ResolveControl } from "./ResolveControl";
import { hasDetails, oneLineSummary } from "./suggestions";

/**
 * §B4.3's row. T8 deferred item 3 (design §7.4): derived off the generated `TaxMapping` (`TaxMappingOut`)
 * rather than hand-declared, so `tsc` rejects a field the wire does not carry (the bug this fix closes:
 * `row.rate` was read here, but the wire only ever had `rate_pct`). `direction`, `vat_only` and `region`
 * are NOT on the wire (`save_tax_resolutions` never persists them) and always render "None" (T9,
 * follow-up (b)); `options` and `message` are enriched web-only from the row's own review item
 * (`ReviewScreen.tsx`).
 */
export type TaxRow = TaxMapping & {
  direction?: string | null;
  vat_only?: boolean | null;
  region?: string | null;
  options?: string[] | null;
  message?: string | null;
  /** xmg-coafast: the OPEN item's own suggestion and one-line summary (web-only enrichment, like `options`). */
  suggested_option?: string | null;
  summary?: string | null;
  /** The open item's severity (web-only enrichment): only a BLOCK choice is drawn with the red edge. */
  severity?: string | null;
};

/** An open row that offers options and suggests none: a genuine choice, listed first. */
function rowNeedsChoice(row: TaxRow): boolean {
  return (row.options ?? []).length > 0 && (row.suggested_option ?? null) === null;
}

/**
 * §B4.3. The server has already filtered the option list by treatment, then by rate and direction (1a §G5).
 * This component renders `options` **in the order it arrives** and neither re-filters nor re-sorts it: the option
 * list is an accounting decision, not a presentation detail.
 */
export function VatQueue({
  rows,
  onResolve,
  disabled,
  targetKey = null,
}: {
  rows: readonly TaxRow[];
  onResolve: (sourceCode: string, option: string) => void;
  disabled: boolean;
  /** xmg-webux: the row "Open the tab to choose" jumped to (highlighted, scrolled to and focused). */
  targetKey?: string | null;
}): JSX.Element {
  const [chosen, setChosen] = useState<Record<string, string>>({});
  // xmg-coafast: the rows that need a real choice first (stable otherwise), so they are not buried.
  const ordered = [...rows.filter(rowNeedsChoice), ...rows.filter((row) => !rowNeedsChoice(row))];

  const columns: readonly Column<TaxRow>[] = [
    { key: "source_code", header: t("mapping.col_source_code"), render: (row) => row.source_code },
    {
      key: "treatment",
      header: t("mapping.col_treatment"),
      render: (row) => row.treatment ?? "",
    },
    {
      key: "rate",
      header: t("mapping.col_rate"),
      numeric: true,
      // T8 deferred item 3: the wire field is `rate_pct` (`TaxMappingOut`) -- rendered verbatim (T1/T2),
      // never parsed or rounded, exactly as the docstring above already required.
      render: (row) => <span data-testid={`tax-rate-${row.source_code}`}>{row.rate_pct ?? ""}</span>,
    },
    {
      key: "direction",
      header: t("mapping.col_direction"),
      render: (row) => row.direction ?? "",
    },
    {
      key: "vat_only",
      header: t("mapping.col_vat_only"),
      render: (row) =>
        row.vat_only === null || row.vat_only === undefined
          ? ""
          : row.vat_only
            ? t("common.yes")
            : t("common.no"),
    },
    { key: "region", header: t("mapping.col_region"), render: (row) => row.region ?? "" },
    {
      key: "target_code",
      header: t("mapping.col_target_code"),
      render: (row) => <span data-testid={`tax-target-${row.source_code}`}>{row.target_code ?? ""}</span>,
    },
    {
      key: "status",
      header: t("mapping.col_status"),
      render: (row) => (
        <>
          <span data-testid={`tax-status-${row.source_code}`}>{row.status ?? ""}</span>
          {rowNeedsChoice(row) ? (
            <Badge tone="warn">
              <span data-testid={`tax-needs-choice-${row.source_code}`}>
                {t("review.needs_choice_title")}
              </span>
            </Badge>
          ) : null}
        </>
      ),
    },
    {
      key: "message",
      header: t("review.col_message"),
      cellClassName: "cell--wide",
      render: (row) => {
        if (row.message === null || row.message === undefined) {
          // xmg-coafast2: an UNUSED code files no review item, so say why in plain words instead of a blank.
          return (
            <span data-testid={`tax-message-${row.source_code}`}>
              {row.status === "UNUSED" ? t("review.tax_unused_reason") : ""}
            </span>
          );
        }
        const item = { summary: row.summary ?? null, message: row.message };
        // xmg-coafast: one plain line, the full message under "Details" (a short message is shown whole).
        return hasDetails(item) ? (
          <>
            <span data-testid={`tax-summary-${row.source_code}`}>{oneLineSummary(item)}</span>
            <details>
              <summary>{t("review.details")}</summary>
              <p data-testid={`tax-message-${row.source_code}`}>{row.message}</p>
            </details>
          </>
        ) : (
          <span data-testid={`tax-message-${row.source_code}`}>{row.message}</span>
        );
      },
    },
    {
      key: "options",
      header: t("mapping.col_options"),
      cellClassName: "cell--wide",
      render: (row) => {
        const options = row.options ?? [];
        const selectId = `vat-option-${row.source_code}`;
        // xmg-coafast: starts on the open item's suggestion, if it has one.
        const picked = chosen[row.source_code] ?? row.suggested_option ?? "";
        return (
          // T8 deferred item 3, critique C3: after a successful resolve, `options` empties out (the item is
          // now resolved) and the select disables -- but `picked` survives the refetch, so without the
          // `options.length === 0` guard the Resolve button stayed enabled with nothing left to POST to (an
          // enabled no-op control, I5's invariant extended to this tab).
          <ResolveControl
            selectId={selectId}
            code={row.source_code}
            options={options}
            picked={picked}
            disabled={disabled || options.length === 0}
            resolveDisabled={disabled || picked === "" || options.length === 0}
            onPick={(option) => setChosen({ ...chosen, [row.source_code]: option })}
            onResolve={() => onResolve(row.source_code, picked)}
          />
        );
      },
    },
  ];

  return (
    <div className="stacktable">
      <Table
        wrap
        caption={t("review.caption_tax")}
        columns={columns}
        rows={ordered}
        rowKey={(row) => row.source_code}
        emptyMessage={t("review.empty")}
        rowClassName={(row) =>
          row.source_code === targetKey
            ? "is-target"
            : rowNeedsChoice(row) && row.severity === "BLOCK"
              ? "is-block"
              : undefined
        }
        rowDataKey={(row) => row.source_code}
      />
    </div>
  );
}
